// components/notifications/NotificationButton.js - Synced with NotificationSettingsPage & Onboarding
import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, ListGroup, Badge, Spinner } from "react-bootstrap";
import { Bell, BellFill, Gear } from 'react-bootstrap-icons';
import { FCM } from "@capacitor-community/fcm";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import './NotificationButton.css';

function NotificationButton({ 
  feedId, 
  feedName, 
  branch, 
  topic = 'General',
  country = 'us',
  variant = 'outline-primary',
  size = 'sm',
  showLabel = false,
  onNotificationStateChange 
}) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  // Map branch to FCM topic - MUST match NotificationSettingsPage & Onboarding
  const getTopicForBranch = (branch) => {
    const prefix = country === 'ca' ? 'Canada' 
      : country === 'mx' ? 'Mexico' 
      : country === 'uk' ? 'UK' 
      : '';
  
    switch (branch.toLowerCase()) {
      case 'legislative':
        return `${prefix}LegislativeNews`;
      case 'executive':
        return `${prefix}ExecutiveNews`;
      case 'judicial':
        return `${prefix}JudicialNews`;
      case 'defense':
      case 'defence':
        return `${prefix}DefenseNews`;
      case 'economy':
        return `${prefix}EconomyNews`;
      case 'environment':
        return `${prefix}EnvironmentNews`;
      case 'foreign affairs':
      case 'foreignaffairs':
        return `${prefix}ForeignAffairsNews`;
      case 'health':
        return `${prefix}HealthNews`;
      case 'elections':
        return `${prefix}ElectionNews`;
      case 'science & tech':
      case 'sciencetech':
      case 'science':
        return `${prefix}ScienceTechNews`;
      case 'spending':
        return `${prefix}SpendingNews`;
      case 'infrastructure':
        return `${prefix}InfrastructureNews`;
      case 'regulations':
        return `${prefix}RegulationsNews`;
      case 'justice':
        return `${prefix}JusticeNews`;
      default:
        return `${prefix}GovGlanceNews`;
    }
  };

  // FCM topic is ALSO the localStorage key - syncs with NotificationSettingsPage
  const fcmTopic = getTopicForBranch(branch);

  // Check Capacitor support
  useEffect(() => {
    const checkCapacitorSupport = () => {
      try {
        const isNative = Capacitor.isNativePlatform();
        if (!isNative) {
          setIsSupported(false);
          return;
        }
        const hasFCM = typeof FCM !== 'undefined' && FCM.subscribeTo;
        const hasPushNotifications = typeof PushNotifications !== 'undefined' && PushNotifications.checkPermissions;
        setIsSupported(isNative && hasFCM && hasPushNotifications);
      } catch (error) {
        setIsSupported(false);
      }
    };
    checkCapacitorSupport();
  }, []);

  // Load subscription state - uses fcmTopic as key (syncs with Settings & Onboarding)
  useEffect(() => {
    const checkSubscription = () => {
      const isTopicSubscribed = localStorage.getItem(fcmTopic) === 'true';
      setIsSubscribed(isTopicSubscribed);
    };

    checkSubscription();

    // Listen for changes from NotificationSettingsPage or Onboarding
    const handleStorageChange = (e) => {
      if (e.key === fcmTopic) {
        setIsSubscribed(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fcmTopic]);

  // Notify parent of state changes
  useEffect(() => {
    if (onNotificationStateChange) {
      onNotificationStateChange(isSubscribed, feedId, feedName, branch, topic);
    }
  }, [isSubscribed, feedId, feedName, branch, topic, onNotificationStateChange]);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  }, []);

  const subscribeToTopic = async () => {
    try {
      if (!isSupported) {
        localStorage.setItem(fcmTopic, 'true');
        window.dispatchEvent(new Event('storage'));
        return true;
      }

      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== 'granted') {
        throw new Error('Permission denied');
      }

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => reject(new Error('Timeout')), 10000);

        PushNotifications.addListener('registration', async (token) => {
          clearTimeout(timeoutId);
          try {
            if (Capacitor.getPlatform() === 'android') {
              await FCM.initialize();
            }
            await FCM.getToken();
            await FCM.subscribeTo({ topic: fcmTopic });
            
            localStorage.setItem(fcmTopic, 'true');
            window.dispatchEvent(new Event('storage'));
            resolve(true);
          } catch (err) {
            reject(err);
          }
        });

        PushNotifications.addListener('registrationError', (err) => {
          clearTimeout(timeoutId);
          reject(err);
        });

        PushNotifications.register();
      });
    } catch (error) {
      console.error('Subscribe error:', error);
      throw error;
    }
  };

  const unsubscribeFromTopic = async () => {
    try {
      localStorage.setItem(fcmTopic, 'false');
      window.dispatchEvent(new Event('storage'));

      if (isSupported) {
        try {
          await FCM.unsubscribeFrom({ topic: fcmTopic });
        } catch (err) {
          console.error('FCM unsubscribe error:', err);
        }
      }
      return true;
    } catch (error) {
      console.error('Unsubscribe error:', error);
      throw error;
    }
  };

  const handleToggle = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      if (isSubscribed) {
        await unsubscribeFromTopic();
        setIsSubscribed(false);
      } else {
        await subscribeToTopic();
        setIsSubscribed(true);
      }
    } catch (error) {
      showToast('Error: ' + error.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Browser fallback message
  if (!isSupported) {
    return (
      <div className="d-flex align-items-center">
        <Button 
          onClick={handleToggle}
          disabled={isLoading}
          variant="link"
          size={size}
          className="notification-btn p-0 border-0"
          title={`${isSubscribed ? 'Unsubscribe from' : 'Subscribe to'} ${feedName} (${fcmTopic})`}
        >
          {isLoading ? (
            <Spinner animation="border" size="sm" className="notification-spinner" />
          ) : (
            isSubscribed ? <BellFill size={18} className="notification-icon" /> : <Bell size={18} className="notification-icon" />
          )}
        </Button>
      </div>
    );
  }

  return (
    <>
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {toasts.map(toast => (
            <div
              key={toast.id}
              style={{
                padding: '12px 20px',
                borderRadius: '8px',
                backgroundColor: toast.type === 'success' ? '#28a745' : 
                               toast.type === 'danger' ? '#dc3545' : '#17a2b8',
                color: 'white',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                minWidth: '250px'
              }}
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}

      <Button 
        onClick={handleToggle}
        disabled={isLoading}
        variant="link"
        size={size}
        className="notification-btn p-0 border-0"
        title={`${isSubscribed ? 'Unsubscribe from' : 'Subscribe to'} ${feedName} (${fcmTopic})`}
      >
        {isLoading ? (
          <Spinner animation="border" size="sm" className="notification-spinner" />
        ) : (
          isSubscribed ? <BellFill size={18} className="notification-icon" /> : <Bell size={18} className="notification-icon" />
        )}
      </Button>
    </>
  );
}

export default NotificationButton;